// @ts-check

const Bluebird = require('bluebird')
const _ = require('lodash')
const cookie = require('js-cookie')
const uuid = require('uuid/v4')
const qs = require('qs')
// @ts-ignore - Remove this once the module has proper type definitions
const { track } = require('client/lib/analytics')

// Type definitions

/** @typedef {(string|number)} UserId */
/** @typedef {string} SessionId */

/**
 * @typedef {Object} User
 * @property {UserId} userId
 * @property {boolean} loggedIn
 * @property {boolean} bot
 */

/**
 * @typedef {string} QueryString
 * @see https://tools.ietf.org/html/rfc3986#section-3.4
 */

/**
 * @typedef {Object.<string,any>} QueryParams
 * @see https://github.com/ljharb/qs#parsing-objects
 */

/** 
 * @typedef {Object} UtmParams
 * @property {string} [utm_source]
 * @property {string} [utm_medium]
 * @property {string} [utm_campaign]
 * @property {string} [utm_term]
 * @property {string} [utm_content]
 * @property {QueryParams} [utm_custom_params]
 * @see https://en.wikipedia.org/wiki/UTM_parameters
 */

// Constants

const SESSION_EXPIRE_DURATION = 30 * 60 * 1000 // 30 min
const SESSION_COOKIE_PREFIX = 'usr_sess:'
const UTM_PREFIX = 'utm_'
const UTM_CUSTOM_PREFIX = UTM_PREFIX + 'mix_'

/**
 * Manages the session cookies associated with a {@link User}.
 * @class 
 */
module.exports = class Session {
  /**
   * @param {User} user
   * @constructor 
   */
  constructor(user) {
    /** @type {UserId} */
    this.userId = _.get(user, 'id')
    /** @type {boolean} */
    this.isLoggedIn = _.get(user, 'loggedIn', false)
    /** @type {boolean} */
    this.isBot = _.get(user, 'bot', false)
    /** @type {SessionId|void} */
    this.initialTempId = cookie.get('temp_id')
    /** @type {(QueryString|void)} */
    this.cachedQueryString = undefined
    /** @type {(UtmParams|void)} */
    this.cachedUtmParams = undefined
  }

  /**
   * Update the persisted session ID for the current user.
   * This will refresh the `ttl` on the current session if it exists. 
   * @returns {SessionId} The session ID for the current user.
   * @public
   */
  update() {
    if (this.isBot) {
      return
    }

    const tempId = cookie.get('temp_id')
    const userSessionId = getSessionId(this.userId)
    const tempSessionId = tempId && getSessionId(tempId)

    this.maybeClearTempId(tempId)
    // The current session (if any) will get renewed immediately after clearing
    clearAllSessionCookies()

    const currentSessionId = userSessionId || tempSessionId || uuid()
    const sessionPersisted = setSessionId(this.userId, currentSessionId)

    /**
     * Don't bother sending tracking if the session did not persist (i.e. cookies disabled),
     * as the session will effectively change every time {@link Session#update} is called.
     */
    if (sessionPersisted) {
      /**
       * Parse and cache the current UTM parameters so that subsequent calls to {@link Session#update}
       * don't try to call {@link Session#trackSessionUpdate} while {@link Session#trackSessionChanged}
       * is happening asynchronously.
       */
      const utmParams = this.getNewUtmParams()
      /**
       * The session is considered "changed" if the user is now logged-in and
       * did not have a session ID for their logged-in user ID, but now does.
       */
      if (this.isLoggedIn && !userSessionId) {
        this.trackSessionChanged(currentSessionId, utmParams)
      } else {
        this.trackSessionUpdate(currentSessionId, utmParams)
      }
    }
  
    return currentSessionId
  }

  /**
   * Add an event listener to check if the session has changed in another window.
   * @param {function} sessionChangedCallback Called when the current session ID changes.
   * @returns {function} Remove event listener and stop watching for session ID changes.
   * @public
   */
  watch(sessionChangedCallback) {
    if (this.isBot) {
      return _.noop
    }

    const { userId } = this
    // Capture the session at initial page load
    let mostRecentSessionId = getSessionId(userId) 
    // @ts-ignore - jQuery is implicitly global
    $(window).on('focus blur', checkIfSessionHasChanged)
    return function () {
      // @ts-ignore - jQuery is implicitly global
      $(window).off('focus blur', checkIfSessionHasChanged)
    }

    function checkIfSessionHasChanged() {
      // Capture the session id on focus/blur
      const currentSessionId = getSessionId(userId)
      // The session may have been affected by other tabs or ttl timeout
      if (currentSessionId !== mostRecentSessionId) {
        sessionChangedCallback()
        mostRecentSessionId = currentSessionId
      }
    }
  }

  /**
   * Clears the anonymous user ID cookie once the user logs in.
   * Does not clear the cookie if it has changed in another tab.
   * @param {UserId} tempId
   * @private
   */
  maybeClearTempId(tempId) {
    if (this.isLoggedIn && tempId && tempId === this.initialTempId) {
      cookie.remove('temp_id')
    }
  }

  /**
   * Parses the current query string into {@link UtmParams}.
   * Caches the parsed result across multiple calls, and
   * returns `undefined` for non-existent or unchanged UTM parameters.
   * @returns {UtmParams|void} "New" UTM parameters or `undefined` if old/empty.
   * @private
   */
  getNewUtmParams() {
    const queryString = location.search
    if (!queryString || queryString === this.cachedQueryString) {
      return
    }
    this.cachedQueryString = queryString
    const utmParams = parseUtmParams(queryString)
    if (!utmParams || _.isEqual(utmParams, this.cachedUtmParams)) {
      return
    }
    this.cachedUtmParams = utmParams
    return utmParams
  }

  /**
   * Sends a `session-changed` tracking call,
   * followed by a `session-update` tracking call.
   * @param {SessionId} sessionId
   * @param {UtmParams|void} utmParams
   * @returns {Bluebird.<any>}
   * @private
   */
  trackSessionChanged(sessionId, utmParams) {
    return track('session-changed', {
      sessionId,
      noThirdPartyTracking: true
    })
    .then(() => {
      return this.trackSessionUpdate(sessionId, utmParams)
    })
  }

  /**
   * Send a `session-update` tracking call.
   * @param {SessionId} sessionId
   * @param {UtmParams|void} utmParams
   * @returns {Bluebird.<any>}
   * @private
   */
  trackSessionUpdate(sessionId, utmParams) {
    if (!utmParams) {
      return Bluebird.resolve()
    }
    return track('session-update', {
      sessionId,
      utmParams,
      noThirdPartyTracking: true
    })
  }
}

/**
 * Retrieve a session ID for a given user ID from the global cookie store.
 * Session cookies are considered expired after the `ttl`.
 * @param {UserId} userId
 * @returns {(SessionId|void)} The session ID or `undefined` if expired.
 */
function getSessionId(userId) {
  const session = cookie.getJSON(SESSION_COOKIE_PREFIX + userId)
  const ttl = _.toSafeInteger(_.get(session, 'ttl'))
  if (ttl > Date.now()) {
    // Session is still valid
    return _.get(session, 'id')
  }
}

/**
 * Create or update a session cookie in the global cookie store.
 * @param {UserId} userId
 * @param {SessionId} sessionId
 * @returns {boolean} Whether the sessionId was successfully persisted.
 */
function setSessionId(userId, sessionId) {
  cookie.set(SESSION_COOKIE_PREFIX + userId, {
    id: sessionId,
    ttl: Date.now() + SESSION_EXPIRE_DURATION
  }, {
    // Set a session-based cookie which gets cleared on browser close.
    expires: null
  })
  return getSessionId(userId) === sessionId
}

/**
 * Remove all session cookies from the global cookie store
 */
function clearAllSessionCookies() {
  for (let cookieName in cookie.get()) {
    if (_.startsWith(cookieName, SESSION_COOKIE_PREFIX)) {
      cookie.remove(cookieName)
    }
  }
}

/**
 * @param {QueryString} queryString A URI Query component string containing UTM parameters.
 * @returns {(UtmParams|void)} The parsed UTM parameters or `undefined` if there are none.
 */
function parseUtmParams(queryString) {
  /** @type {QueryParams} */
  const queryParams = qs.parse(queryString, {
    ignoreQueryPrefix: true
  })
  /** @type {QueryParams} */
  const customParams = {}
  /** @type {UtmParams} */
  const utmParams = _.transform(queryParams, (
    /** @type {UtmParams} */ obj,
    /** @type {any} */ value,
    /** @type {string} */ key
  ) => {
    if (!_.startsWith(key, UTM_PREFIX)) {
      // Ignore non-UTM query parameters
      return
    }
    if (_.startsWith(key, UTM_CUSTOM_PREFIX)) {
      const customKeySuffix = key.slice(UTM_CUSTOM_PREFIX.length)
      customParams[customKeySuffix] = value
    } else {
      // @ts-ignore - The startsWith check at runtime is good enough
      obj[key] = value
    }
  })
  if (!_.isEmpty(customParams)) {
    utmParams['utm_custom_params'] = customParams
  }
  return _.isEmpty(utmParams) ? undefined : utmParams
}
